import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 to-rose-50 pt-16">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl font-playfair font-bold text-charcoal-900 mb-8">
            Alera – Privacy Policy
          </h1>
          
          <div className="prose prose-lg max-w-none text-charcoal-700">
            <p className="text-lg leading-relaxed mb-8">
              At Alera, your privacy is important to us. This Privacy Policy explains how we collect, 
              use, and protect your personal information when you visit our website, use our services, 
              or purchase digital products.
            </p>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  1. Information We Collect
                </h2>
                <p className="mb-4">We may collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Personal information (name, email address, phone number, date of birth, account details).</li>
                  <li>Health and wellness information you provide during signup, including allergies, health goals, and fitness level.</li>
                  <li>Payment information when you purchase subscriptions or digital products (processed securely by Stripe; card details are never stored by us).</li>
                  <li>Information you provide voluntarily in the community forum, goal-setting features, check-in journal, or cycle tracker.</li>
                  <li>Technical information (browser type, IP address, cookies, site usage).</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="mb-4">We use your information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide access to recipes, workouts, resources, and community features.</li>
                  <li>Process payments and manage subscriptions.</li>
                  <li>Improve our website, programs, and member experience.</li>
                  <li>Communicate with you about updates, resources, or promotions (you can unsubscribe anytime).</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  3. Sharing Your Information
                </h2>
                <p className="mb-4">We do not sell or rent your personal information. We may share limited information with trusted third-party service providers, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Stripe</strong> – for secure payment processing and subscription management.</li>
                  <li><strong>Supabase</strong> – for secure data storage and user authentication.</li>
                  <li><strong>Brevo</strong> – for sending transactional emails (e.g. password setup, account notifications).</li>
                  <li><strong>Kit (ConvertKit)</strong> – for email marketing and welcome sequences (you can unsubscribe at any time).</li>
                  <li>Legal authorities if required by law.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  4. Cookies & Tracking
                </h2>
                <p>
                  We use cookies to improve user experience, track site usage, and personalize content. 
                  You can disable cookies in your browser settings, but some features may not function.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  5. Data Security
                </h2>
                <p>
                  We take reasonable measures to protect your information. However, no online system 
                  is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  6. Your Rights
                </h2>
                <p>
                  Depending on your country, you may have the right to access, correct, or delete 
                  your personal data. To make a request, contact us at{' '}
                  <a 
                    href="mailto:hello@alera.co.nz" 
                    className="text-sage-600 hover:text-sage-700 underline"
                  >
                    hello@alera.co.nz
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  7. Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. Updates will be posted here 
                  with a new effective date.
                </p>
              </section>
            </div>

            <div className="mt-12 p-6 bg-sage-50 rounded-lg border border-sage-200">
              <p className="text-sm text-charcoal-600">
                <strong>Last Updated:</strong> February 2026
              </p>
              <p className="text-sm text-charcoal-600 mt-2">
                If you have any questions about this Privacy Policy, please contact us at{' '}
                <a 
                  href="mailto:hello@alera.co.nz" 
                  className="text-sage-600 hover:text-sage-700 underline"
                >
                  hello@alera.co.nz
                </a>.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
